<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getColorFromScore, stripHtmlTags } from '@/utils'
import UIKit from '@pocketprep/ui-kit'
import ContentTab from '@/components/Content/ContentTab.vue'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import StudentQuestionRow from '@/components/StudentQuestionRow.vue'
import SectionHead from '@/components/Stats/SectionHead.vue'
import type { Study } from '@pocketprep/types'
import type { TStudentQuestionRow, TStudentQuestions } from '@/store/types'

type TStudyActivity = {
    icon: string
    name: string
    iconColor: string
    date: string
    year?: string
    score: number | string
    scoreRange: string
    answers: Study.Cloud.IQuizAnswer[]
    quizId: string
    durationSeconds: number
    levelUpQuizProgress?: Study.Class.LevelUpProgress
}

type TMockExamSubjects = {
    [key: string]: {
        name: string
        correctCount: number
        answeredCount: number
        score: number
    }
}

const {
    SidePanel,
    Icon,
    Table,
    EmptyState,
    Button: PocketButton,
} = UIKit

const props = defineProps<{
    student: string
    examActivity: TStudyActivity
    trapFocus?: boolean
}>()

const emit = defineEmits<{ close: [value?: boolean] }>()

const route = useRoute()
const router = useRouter()

const activeTab = ref<'Answered' | 'Flagged' | 'Incorrect' | 'Correct'>('Answered')
const quizId = ref<string | null>(null)
const expandSubjects = ref(true)

const tableColumns = [{
    name: ' ',
    propName: 'question',
    isSortDisabled: true,
}, {
    name: ' ',
    propName: 'numStudentFlagged',
    isSortDisabled: true,
}]

const studentStats = computed(() => statsModule.getters.getCurrentStudentStats())
const examActivityMode = computed(() => props.examActivity.name)
const examActivityScore = computed(() => props.examActivity.score as number)
const currentExamQuestionsBySerial = computed(() => examModule.getters.getCurrentExamQuestionsBySerial())
const studentFlaggedQuestions = computed(() => studentStats.value.flaggedQuestions)

const isQuestionFlagged = (serial: string) =>
    !!studentFlaggedQuestions.value.find(question => question.serial === serial)

const studentQuestionsForActivity = computed(() => {
    const questionsForActivity: TStudentQuestionRow[] = []
    const answers = props.examActivity.answers || []
    answers.forEach(quizAnswer => {
        const q = currentExamQuestionsBySerial.value[quizAnswer.questionSerial]
        if (q) {
            const scenarioPartLabel = examModule.getters.getScenarioPartLabel(q)
            const s = q.subject as Study.Class.SubjectJSON
            questionsForActivity.push({
                id: Math.random(),
                serial: q.serial,
                subject: s.name,
                prompt: q.prompt,
                isFlagged: isQuestionFlagged(q.serial),
                isCorrect: quizAnswer.isCorrect,
                isAnswered: true,
                tabIndex: 0,
                scenarioPartLabel,
            })
        }
    })
    return questionsForActivity
})

const sortedMockExamSubjects = computed(() =>
    studentQuestionsForActivity.value.reduce<TMockExamSubjects>((acc, question) => {
        if (!acc[question.subject]) {
            acc[question.subject] = {
                name: question.subject,
                correctCount: 0,
                answeredCount: 0,
                score: 0,
            }
        }
        const subject = acc[question.subject]
        if (!subject) {
            return acc
        }
        if (question.isCorrect) {
            subject.correctCount++
        }
        if (question.isAnswered) {
            subject.answeredCount++
        }
        subject.score = Math.round(
            (subject.correctCount / subject.answeredCount) * 100
        )
        return acc
    }, {} as TMockExamSubjects)
)

const questions = computed((): TStudentQuestions =>
    studentQuestionsForActivity.value.reduce<TStudentQuestions>((acc, question) => {
        const serial = question.serial
        const isFlagged = isQuestionFlagged(serial)
        const isAnswered = question.isAnswered
        const isCorrect = question.isCorrect
        const questionPrompt = stripHtmlTags(question.prompt)
        const questionRow = {
            id: Math.random(),
            serial: question.serial,
            subject: question.subject,
            prompt: questionPrompt,
            isFlagged,
            isCorrect: question.isCorrect,
            isAnswered: question.isAnswered,
            tabIndex: 0,
            scenarioPartLabel: question.scenarioPartLabel,
        }
        if (isFlagged) {
            acc.Flagged?.push(questionRow)
        }
        if (isAnswered) {
            acc.Answered?.push(questionRow)
        }
        if (!isCorrect && isAnswered) {
            acc.Incorrect?.push(questionRow)
        }
        if (isCorrect) {
            acc.Correct?.push(questionRow)
        }
        return acc
    }, { Answered: [], Flagged: [], Incorrect: [], Correct: [] })
)

const clickChangeTab = (tab: 'Answered' | 'Flagged' | 'Incorrect' | 'Correct') => {
    activeTab.value = tab
}

const toggleExpandSubjects = () => {
    expandSubjects.value = !expandSubjects.value
}

const formattedDuration = computed(() => {
    const durationSeconds = props.examActivity.durationSeconds
    if (durationSeconds < 60) {
        return `${durationSeconds} sec`
    }
    if (durationSeconds > 60 && durationSeconds < 3600) {
        const min = Math.round(durationSeconds / 60)
        return `${min} min`
    }
    if (durationSeconds > 3600) {
        const hours = Math.floor(durationSeconds / 3600)
        const min = Math.round((durationSeconds - (hours * 3600)) / 60)
        return `${hours}h ${min}m`
    }
    return '0'
})

const emitClose = () => {
    emit('close', true)
}

const rowClicked = (row: { serial: string }) => {
    router.push({
        name: route.name as string,
        params: { ...route.params },
        query: {
            ...route.query,
            questionSerial: row.serial,
            quizId: quizId.value ?? undefined,
            type: activeTab.value.toLocaleLowerCase(),
        },
    })
}

onMounted(() => {
    quizId.value = props.examActivity.quizId
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            class="mock-exam-activity"
            width="medium"
            name=""
            :trap-focus="trapFocus"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <div
                class="mock-exam-activity__top-container"
            >
                <div
                    class="mock-exam-activity__quiz-results"
                >
                    <div
                        class="mock-exam-activity__quiz-results-name-container"
                    >
                        <div
                            v-if="examActivityMode !== 'Mock Exam'"
                            class="mock-exam-activity__quiz-results-name"
                        >
                            {{ examActivityMode }}
                        </div>
                        <div
                            class="mock-exam-activity__quiz-results-name"
                        >
                            Mock
                        </div>
                        <div
                            class="mock-exam-activity__quiz-results-name"
                        >
                            Exam
                        </div>
                    </div>
                    <div
                        class="mock-exam-activity__quiz-results-stat mock-exam-activity__quiz-results-score-color"
                        :class="getColorFromScore(examActivityScore)"
                    >
                        <span>
                            {{ examActivityScore }}%
                        </span>
                        <label>Exam Score</label>
                    </div>
                    <div
                        class="mock-exam-activity__quiz-results-stat mock-exam-activity__quiz-results-unanswered"
                    >
                        <span>
                            {{ questions['Correct']?.length }} / {{ questions['Answered']?.length }}
                        </span>
                        <label>Answered Correctly</label>
                    </div>
                </div>
            </div>
            <div
                v-if="examActivityMode === 'Mock Exam' && expandSubjects"
                ref="mockExamContainer"
                class="mock-exam-activity__mock-exam-container"
            >
                <SectionHead
                    title="Subject Scores"
                    icon="subject"
                    class="mock-exam-activity__subject-scores-head"
                >
                    <div class="mock-exam-activity__head-ratio-label">
                        Correct / Answered
                    </div>
                    <div class="mock-exam-activity__head-score-label">
                        Score
                    </div>
                </SectionHead>
                <ul
                    v-if="examActivityMode === 'Mock Exam'"
                    class="mock-exam-activity__subject-scores"
                >
                    <li
                        v-for="subject in sortedMockExamSubjects"
                        :key="subject.name"
                    >
                        <div class="mock-exam-activity__subject-name">
                            {{ subject.name }}
                        </div>
                        <div class="mock-exam-activity__subject-ratio">
                            {{ subject.correctCount }} / {{ subject.answeredCount }}
                        </div>
                        <div class="mock-exam-activity__subject-score-percent">
                            {{ subject.score }}
                        </div>
                        <span class="mock-exam-activity__percentage-sign">%</span>
                    </li>
                </ul>
            </div>
            <div
                class="mock-exam-activity__button"
            >
                <PocketButton
                    v-if="examActivityMode === 'Mock Exam'"
                    type="tertiary-small"
                    class="mock-exam-activity__expand"
                    :class="{
                        'mock-exam-activity__expand--expanded': expandSubjects,
                        'mock-exam-activity__expand--collapsed': !expandSubjects,
                    }"
                    @click="toggleExpandSubjects"
                >
                    {{ expandSubjects ? 'Collapse Subject Scores' : 'Expand Subject Scores' }}
                    <Icon type="accordionArrow" />
                </PocketButton>
            </div>

            <div
                class="mock-exam-activity__tabs"
                role="tablist"
            >
                <ContentTab
                    key="Answered"
                    :is-active="activeTab === 'Answered'"
                    label="Answered"
                    :count="questions['Answered']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'Answered' ? 'true' : 'false'"
                    @keydown.enter="clickChangeTab('Answered')"
                    @click="clickChangeTab('Answered')"
                />
                <ContentTab
                    key="Flagged"
                    :is-active="activeTab === 'Flagged'"
                    label="Flagged"
                    :count="questions['Flagged']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'Flagged' ? 'true' : 'false'"
                    @keydown.enter="clickChangeTab('Flagged')"
                    @click="clickChangeTab('Flagged')"
                />
                <ContentTab
                    key="Incorrect"
                    :is-active="activeTab === 'Incorrect'"
                    label="Incorrect"
                    :count="questions['Incorrect']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'Incorrect' ? 'true' : 'false'"
                    @keydown.enter="clickChangeTab('Incorrect')"
                    @click="clickChangeTab('Incorrect')"
                />
                <ContentTab
                    key="Correct"
                    :is-active="activeTab === 'Correct'"
                    label="Correct"
                    :count="questions['Correct']?.length"
                    role="tab"
                    :aria-selected="activeTab === 'Correct' ? 'true' : 'false'"
                    @keydown.enter="clickChangeTab('Correct')"
                    @click="clickChangeTab('Correct')"
                />
            </div>
            <div
                class="mock-exam-activity__questions"
                :style="{
                    // Should be a function of how many visible subjects there are
                    // marginTop: marginTopQuestions,
                    // height: heightQuestions,
                }"
            >
                <div
                    v-if="questions['Answered']?.length === 0 && activeTab === 'Answered'"
                    class="mock-exam-activity__empty"
                >
                    <EmptyState
                        class="mock-exam-activity__empty-state"
                        state="answered"
                        title="0 Answered Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Flagged']?.length === 0 && activeTab === 'Flagged'"
                    class="mock-exam-activity__empty"
                >
                    <EmptyState
                        class="mock-exam-activity__empty-state"
                        state="flagged"
                        title="0 Flagged Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Incorrect']?.length === 0 && activeTab === 'Incorrect'"
                    class="mock-exam-activity__empty"
                >
                    <EmptyState
                        class="mock-exam-activity__empty-state"
                        state="incorrect"
                        title="0 Incorrect Questions"
                    />
                </div>
                <div
                    v-else-if="questions['Correct']?.length === 0 && activeTab === 'Correct'"
                    class="mock-exam-activity__empty"
                >
                    <EmptyState
                        class="mock-exam-activity__empty-state"
                        state="correct"
                        title="0 Correct Questions"
                    />
                </div>
                <Table
                    v-if="studentQuestionsForActivity.length"
                    class="mock-exam-activity__table"
                    theme="open"
                    :sort-toggle-order="[ 1, -1 ]"
                    :rows="questions[activeTab] || []"
                    :columns="tableColumns"
                    :show-header="false"
                    :table-content-styles="{
                        boxSizing: 'none',
                    }"
                    :table-row-styles="{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '12px 16px 12px 30px',
                        cursor: 'pointer',
                    }"
                    @rowClicked="rowClicked({serial: $event.serial as string})"
                >
                    <template #tableCellValue="{ row, column }">
                        <StudentQuestionRow
                            class="mock-exam-activity__question-row"
                            :row="row"
                            :column="column"
                        />
                    </template>
                </Table>
            </div>
            <template #sidePanelTitle>
                <div
                    class="mock-exam-activity__title"
                >
                    <div
                        class="mock-exam-activity__name"
                    >
                        {{ student }}
                    </div>
                    <div class="mock-exam-activity__bullet">
                        •
                    </div>
                    <div
                        class="mock-exam-activity__date"
                    >
                        {{ examActivity.date }}, {{ examActivity.year }}
                    </div>
                    <div

                        v-if="examActivity.name === 'Timed Quiz' || examActivity.name === 'Mock Exam'"
                        class="mock-exam-activity__time"
                    >
                        <div
                            class="mock-exam-activity__bullet"
                        >
                            •
                        </div>
                        <Icon
                            class="mock-exam-activity__timer-icon"
                            type="timer"
                        />
                        <div
                            class="mock-exam-activity__duration"
                        >
                            {{ formattedDuration }}
                        </div>
                    </div>
                </div>
            </template>
            <template #sidePanelFooter>
                <div />
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.mock-exam-activity {
    :deep(*) {
        .uikit-side-panel__title-inner {
            font-size: 15px;
            font-weight: 500;
        }
        .uikit-side-panel__head {
            border-bottom: none;
            padding: 28px 0 34px 31px;
        }

        .uikit-side-panel__content {
            padding: 0;
        }

        .student-question-row__prompt {
            width: 356px;
        }

        .uikit-table__rows:nth-child(1) {
            height: 0;
            border-bottom: 1px;
        }

        .uikit-table__rows:nth-child(2) {
            // If mock exam, the max height here should be slightly different
            max-height: 30%;
        }
        .content-tab {
            border-bottom: 4px solid $barely-background;
            &--active {
                border-bottom-color: $brand-black;
                color: $brand-black !important;
            }
        }

        .section-head__title {
            max-width: 200px;
        }
    }

    &__top-container {
        background-color: $barely-background;
        position: sticky;
        top: 0;
        z-index: 1;
    }

    &__tabs {
        display: flex;
        background-color: $barely-background;
        border-bottom: 1px solid $gray-divider;
        padding: 35px 0 0 29px;
        position: sticky;
        top: 60px;
        z-index: 1;
    }

    &__title {
        display: flex;
        font-size: 15px;
        padding-top: 9px;
        color: $ash;
    }

    &__time {
        display: flex;
    }

    &__bullet {
        padding: 3px 8px;
        font-size: 11px;
        color: $ash;
    }

    &__timer-icon {
        padding: 0.5px 5px 0 0;
        color: $ash;
    }

    &__date {
        font-size: 15px;
        color: $ash;
    }

    &__empty {
        background-color: $white;
    }

    &__empty-state {
        margin: 40px auto 0px auto;
    }

    &__subject-scores-head {
        justify-content: start;
        align-items: flex-end;
    }

    &__mock-exam-container {
        padding: 20px 31px 20px 29px;
        background-color: $barely-background;
    }

    &__quiz-results {
        display: flex;
        padding: 0 0 0 31px;
    }

    &__questions {
        background-color: $white;
        overflow-y: scroll;
    }

    &__quiz-results-name {
        font-size: 22px;
        font-weight: 600;
        line-height: 30px;
        width: 190px;
        display: flex;
    }

    &__quiz-results-unanswered {
        min-width: 130px;
    }

    &__quiz-results-stat {
        font-size: 24px;
        text-align: center;
        letter-spacing: 0.24px;
        width: 100px;
        padding-top: 8px;

        &:last-child {
            margin-right: 0;
        }

        span {
            font-weight: 600;
        }

        label {
            white-space: nowrap;
        }
    }

    &__quiz-results-score-color {
        position: relative;

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 0px;
            width: 94px;
            height: 44px;
            margin-left: -52px;
        }

        &.green::before {
            background: url('@/assets/images/highlight-green.svg') no-repeat center center;
        }

        &.red::before {
            background: url('@/assets/images/highlight-red.svg') no-repeat center center;
        }

        &.yellow::before {
            background: url('@/assets/images/highlight-yellow.svg') no-repeat center center;
        }
    }

    &__head-ratio-label {
        flex: 1;
        text-align: right;
        margin-right: 19px;
        font-size: 13px;
        color: $moonlit-ocean;
    }

    &__head-score-label {
        max-width: 34px;
        font-size: 13px;
        color: $moonlit-ocean;
        justify-content: flex-end;
    }

    &__subject-name {
        font-size: 15px;
        line-height: 20px;
        flex: 2;
        min-width: 280px;
        color: $brand-black;
    }

    &__subject-ratio {
        font-weight: 500;
        text-align: right;
        min-width: 53px;
        font-size: 16px;
        letter-spacing: -0.3px;
        color: $brand-black;
    }

    &__subject-score-percent {
        font-weight: 600;
        font-size: 16px;
        margin-right: 5px;
        text-align: right;
        width: 40px;
        color: $brand-black;
    }

    &__percentage-sign {
        font-size: 16px;
        font-weight: 500;
        margin-left: -3px;
    }

    &__button {
        background-color: $barely-background;
    }

    &__expand {
        margin: 0 0 0 32px;

        &--expanded {
            svg {
                transform: rotate(180deg);
            }
        }

        &--collapsed {
            padding-top: 20px;
        }
    }

    label {
        font-size: 12px;
        line-height: 15.71px;
        display: block;
        margin-top: 5px
    }

    ul {
        padding: 0;
        margin: 0;
        list-style-type: none;

        li {
            padding: 11px 0px 10px 3px;
            margin: 0;
            color: $slate-03;
            display: flex;
            width: 100%;
            position: relative;
            align-items: center;
            border-bottom: 1px solid $gray-divider;
            height: 18.45px;

            &::before {
                content: '';
                width: calc(100% + 28px);
                height: 100%;
                z-index: -1;
                position: absolute;
                left: -14px;
                top: 0;
                display: none;
            }

            &:hover::before {
                display: block;
            }
        }
    }
}
</style>
